import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, LinearProgress, Stack, Tooltip, Typography, alpha } from '@mui/material';
import { RocketLaunchRounded } from '@mui/icons-material';
import { usePermissions } from '../../hooks/use-permissions';
import { useSetupProgress } from '../../hooks/use-setup-progress';

/**
 * Compact setup-progress entry point in the sidebar. Visible only to admins
 * while essential setup is incomplete. Clicking opens the Setup Center.
 */
export function SetupProgressWidget({ collapsed }: { collapsed: boolean }) {
  const { t } = useTranslation('setup');
  const navigate = useNavigate();
  const { isAdmin } = usePermissions();
  const { loading, pct } = useSetupProgress();

  // Hide for non-admins, while loading, or once essentials are complete.
  if (!isAdmin || loading || pct >= 100) return null;

  if (collapsed) {
    return (
      <Tooltip title={`${t('widget.title')} · ${t('pctComplete', { pct })}`} placement="right">
        <Box
          onClick={() => navigate('/empezar')}
          sx={{
            mx: 'auto',
            mb: 1,
            width: 36,
            height: 36,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'primary.main',
            bgcolor: (th) => alpha(th.palette.primary.main, 0.12),
          }}
        >
          <RocketLaunchRounded fontSize="small" />
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box
      onClick={() => navigate('/empezar')}
      sx={{
        mx: 2,
        mb: 1.5,
        p: 1.5,
        borderRadius: 2,
        cursor: 'pointer',
        border: (th) => `1px solid ${alpha(th.palette.primary.main, 0.3)}`,
        bgcolor: (th) => alpha(th.palette.primary.main, 0.08),
        transition: 'background-color .15s',
        '&:hover': { bgcolor: (th) => alpha(th.palette.primary.main, 0.16) },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" mb={0.75}>
        <RocketLaunchRounded fontSize="small" sx={{ color: 'primary.main' }} />
        <Typography variant="caption" fontWeight={700} sx={{ flex: 1 }} noWrap>
          {t('widget.title')}
        </Typography>
        <Typography variant="caption" color="primary.main" fontWeight={700}>
          {pct}%
        </Typography>
      </Stack>
      <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 3 }} />
    </Box>
  );
}
